import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, CircularProgress, Alert } from '@mui/material';
import { db } from '../../../firebase';
import { collection, getDocs, doc, writeBatch } from 'firebase/firestore';
import Fuse from 'fuse.js';

function MapTeamsModal({ open, onClose, teams }) {
  const [apiTeams, setApiTeams] = useState([]);
  const [mappings, setMappings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (open) {
      fetchApiTeams();
    }
  }, [open]);

  useEffect(() => {
    if (teams.length > 0 && apiTeams.length > 0) {
      proposeMappings();
    }
  }, [teams, apiTeams]);

  const fetchApiTeams = async () => {
    setIsLoading(true);
    const apiTeamsCollectionRef = collection(db, 'apiTeams');
    const apiTeamsSnapshot = await getDocs(apiTeamsCollectionRef);
    const apiTeamsData = apiTeamsSnapshot.docs.map(doc => doc.data().name).sort();
    setApiTeams(apiTeamsData);
    setIsLoading(false);
  };

  const proposeMappings = () => {
    const fuse = new Fuse(apiTeams, { includeScore: true, threshold: 0.4 });
    const initialMappings = {};
    teams.forEach(team => {
      const result = fuse.search(team.name);
      initialMappings[team.id] = result.length > 0 && result[0].score < 0.4 ? result[0].item : (team.apiTeamName || '');
    });
    setMappings(initialMappings);
  };

  const handleMappingChange = (teamId, value) => {
    setMappings(prev => ({ ...prev, [teamId]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      const batch = writeBatch(db);
      teams.forEach(team => {
        if (mappings[team.id]) {
          const teamRef = doc(db, 'teams', team.id);
          batch.update(teamRef, { apiTeamName: mappings[team.id] });
        }
      });
      await batch.commit();
      onClose();
    } catch (error) {
      console.error('Error saving team mappings:', error);
      setSaveError(`Failed to save mappings: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Map Team Names to API</DialogTitle>
      <DialogContent>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
        {isLoading ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Your Team Name</TableCell>
                  <TableCell>API Team Name (Select to change)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map(team => (
                  <TableRow key={team.id}>
                    <TableCell>{team.name}</TableCell>
                    <TableCell>
                      <Select
                        value={mappings[team.id] || ''}
                        onChange={(e) => handleMappingChange(team.id, e.target.value)}
                        fullWidth
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {apiTeams.map(apiTeam => (
                          <MenuItem key={apiTeam} value={apiTeam}>
                            {apiTeam}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button onClick={handleSave} color="primary" loading={isSaving}>Save Mappings</Button>
      </DialogActions>
    </Dialog>
  );
}

export default MapTeamsModal;